import { useIntl } from '@capsicum/common';
import { Badge, useRouter } from '@capsicum/common-web';
import { type NCWalletTokenCardsProps, useEventsPerPartner } from '@capsicum/features/nc-wallet/shared';
import { MarketFanTokenCard } from '@capsicum/socios-core-web';
import { useMemo } from 'react';
import styled from 'styled-components';

import { useNCWalletTokenCards } from '../../hooks/useNCWalletTokenCards';

import { Title, TokenCards, Wrapper } from './NCWalletTokenCards.styled';
import { NCWalletTokenListCardWithStaking } from './NCWalletTokenListCardWithStaking';

export const NCWalletTokenCardsComponent = ({
	entries,
	name,
	onBodyClick,
	onLogoClick,
	showNewsBadge,
	title
}: NCWalletTokenCardsProps) => {
	const { $t } = useIntl();
	const { push } = useRouter();
	const { createBodyClickListener, createLogoClickListener } = useNCWalletTokenCards({
		name,
		onBodyClick,
		onLogoClick
	});

	const partnerIds = useMemo(
		() =>
			entries
				.map(entry => (entry.type === 'balance' && entry.tokenType === 'partner' ? entry.partnerID : ''))
				.filter(Boolean),
		[entries]
	);
	const { eventsPerPartner, getFormattedEventNumber } = useEventsPerPartner({ disabled: !showNewsBadge, partnerIds });

	const tokenCards = useMemo(
		() =>
			entries.map(entry => {
				if (entry.type === 'no-market') {
					const { partnerID, title, tokenImgUrl } = entry;

					return (
						<MarketFanTokenCard
							isBuyEnabled={false}
							isBuyLoading={false}
							isSellEnabled={false}
							isSellLoading={false}
							key={partnerID}
							logo={tokenImgUrl}
							name={title}
							testID={`ncwPartner-${title}-card`}
							onPartnerLogoClick={() => push(`/partner/${partnerID}`)}
						/>
					);
				}

				const { shortCode, ...props } = entry;
				const partnerEvents =
					showNewsBadge && entry.tokenType === 'partner' ? eventsPerPartner?.[entry.partnerID] || 0 : 0;

				return (
					<S.NCWalletTokenListCardWrapper key={shortCode}>
						<NCWalletTokenListCardWithStaking
							{...props}
							entry={entry}
							isExternalToken={entry.tokenType === 'external-token'}
							key={shortCode}
							testID={`ncwToken-${shortCode}-card`}
							variant="token"
							onBodyClick={createBodyClickListener(entry)}
							onIconClick={createLogoClickListener(entry)}
						/>
						{partnerEvents > 0 && (
							<S.NCWalletTokenListCardBadgeWrapper>
								<Badge label={getFormattedEventNumber(partnerEvents)} />
							</S.NCWalletTokenListCardBadgeWrapper>
						)}
					</S.NCWalletTokenListCardWrapper>
				);
			}),
		[push, entries, createLogoClickListener, createBodyClickListener]
	);

	return (
		entries.length > 0 && (
			<Wrapper>
				<Title variant="text5">{$t(title)}</Title>
				<TokenCards>{tokenCards}</TokenCards>
			</Wrapper>
		)
	);
};

namespace S {
	export const NCWalletTokenListCardWrapper = styled.div`
		position: relative;
	`;

	export const NCWalletTokenListCardBadgeWrapper = styled.div`
		position: absolute;
		top: -8px;
		right: -8px;
		z-index: 1;
	`;
}
